"use client";
import JSONViewer from "@/components/ui/json-viewer";
import { StatementContextPanelProps } from "./statement-context-panel.types";
import { FC } from "react";
import { useAppStore } from "@/store/appStore";
import { ContextPanel } from "@/app/report/components/page";
import { CurrencyCode } from "@/components/ui/flag/flag";
import dayjs from "dayjs";
import { CurrencyValue } from "@/components/ui/currency-value";
import { PDFViewer, ScrollArea } from "@/components/ui";
import { useMeasure } from "@uidotdev/usehooks";
import { isImage, isPdf } from "@/utils/file-type";
import { ArrowRight, CalendarArrowDown, CalendarArrowUp } from "lucide-react";
import { Tag } from "@/components/ui";

const StatementContextPanel: FC<StatementContextPanelProps> = () => {
  const [ref, { width, height }] = useMeasure();
  const selectedAccount = useAppStore((s) => s.selectedAccount);
  const selectedStatement = useAppStore((s) => s.selectedStatement);

  // Debug logs (optional, can be removed)
  // console.log('StatementContextPanel', selectedAccount, selectedStatement);
  // console.log('selectedStatement?.accountNumber', selectedStatement?.accountNumber);
  /*

    const updateUserTransaction = (queryClient: QueryClient, selectedTransaction: TransactionExtended, updates: Partial<Transaction>) => {
    queryClient.setQueryData(['user'], (user: UserExtended) => {
        if (!user) return user;
        return {
            ...user,
            selectedReport: {
                ...user.selectedReport,
                transactions: user.selectedReport?.transactions.map((t: TransactionExtended) =>
                    t.id === selectedTransaction.id ? { ...t, ...updates } : t
                ),
            },
        };
    });
};

    const queryClient = useQueryClient()
    const transactionUpdateMutation = useMutation({
        mutationFn: async ({ endpoint, payload }: { endpoint: string, payload: TransactionUpdatePayload}) => {
            console.log('API:TRANSACTION:UPDATE', endpoint, 'start');
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ ...payload, transactionId: transaction.id }),
            });

            if (!response.ok) {
                throw new Error('Failed to update transaction status');
            }

            const data = await response.json();
            console.log('API:TRANSACTION:UPDATE', endpoint, 'success', data);
            return data;
        },
        onSuccess: (data) => {
            if(selectedTransaction){
                updateUserTransaction(queryClient, selectedTransaction, data.transaction);
            }
        },
        onError: (error) => {
            console.error('API:TRANSACTION:UPDATE', 'error', error);
        },
    });

    const onStatusSelect = async (status: WorkflowStatus) => {
        if(selectedTransaction){
            updateUserTransaction(queryClient, selectedTransaction, { workflowStatus: status });
            transactionUpdateMutation.mutate({ 
                endpoint: '/api/transaction/update/status', 
                payload: { workflowStatus: status } 
            });
        }
    }

    const onPurposeSelect = (purpose: TransactionPurpose) => {
        if(selectedTransaction){
            updateUserTransaction(queryClient, selectedTransaction, { purpose });
            transactionUpdateMutation.mutate({ 
                endpoint: '/api/transaction/update/purpose', 
            payload: { purpose } 
            });
        }
    }

    const onInvoiceSelect = (invoiceId: string) => {
        if(selectedTransaction){
            updateUserTransaction(queryClient, selectedTransaction, { invoiceIds: [...selectedTransaction.invoiceIds, invoiceId] });
            transactionUpdateMutation.mutate({ 
                endpoint: '/api/transaction/update/invoice', 
            payload: { invoiceId } 
            });
        }
    }

    const onNoteChange = (note: string) => {
        if(selectedTransaction){
            updateUserTransaction(queryClient, selectedTransaction, { note });
            transactionUpdateMutation.mutate({ 
                endpoint: '/api/transaction/update/note', 
                payload: { note } 
            });
        }
    }
       

    // Conditional Match Tab Sections 
    const matchTabSections = [
        {
            title: 'Invoice',
            content: <>
                {selectedTransaction && <InvoiceSearch transaction={selectedTransaction} onSelect={onInvoiceSelect} />}
                {selectedTransaction && selectedTransaction.invoices && <InvoiceList invoices={selectedTransaction.invoices as InvoiceExtended[]} />}
            </>
        },
        {
            title: 'Purpose',
            content: <PurposeSelector onChange={onPurposeSelect} />
        },
        {
            title: 'Status',
            content: <StatusSelector onChange={onStatusSelect} />
        },
        
    ];

    if(selectedTransaction){
        matchTabSections.push({
            title: 'Notes',
            content: <TransactionNotes onChange={onNoteChange} note={selectedTransaction.note || ''} />
        });
    }

    const remittanceInfo = (selectedTransaction && selectedTransaction.remittanceInformation ? selectedTransaction.remittanceInformation.trim() : '');
    const additionalInfo = (selectedTransaction && selectedTransaction.remittanceInformation && selectedTransaction.additionalInformation ? `(${selectedTransaction.additionalInformation.toLowerCase().trim()})` : !remittanceInfo && selectedTransaction && selectedTransaction.additionalInformation && selectedTransaction.additionalInformation.toLowerCase().trim());
 */
  return (
    <ContextPanel
      header={{
        image: {
          type: "avatar",
          source: selectedAccount?.institution.logo,
          label: selectedAccount ? selectedAccount.institution.name : "???",
          fallback: selectedAccount?.institution.name
            ? selectedAccount?.institution.name.charAt(0)
            : "",
        } /*{
                type: 'currency',
                label: selectedAccount ? selectedAccount.currency as CurrencyCode : '???' ,
                source: selectedAccount?.currency as CurrencyCode,
                size: 'L',
                fallback: selectedAccount?.currency || '???'
            }*/,
        title: selectedStatement?.accountNumber || "No Account Number",
        subtitle: (
          <>
            <CurrencyValue
              size={"S"}
              currency={
                selectedStatement && selectedStatement.currency
                  ? (selectedStatement.currency as CurrencyCode)
                  : undefined
              }
            />
            <div className="ml-2 text-xs text-gray-500 flex flex-row gap-xs items-center">
              <Tag
                icon={CalendarArrowDown}
                label={
                  selectedStatement
                    ? dayjs(selectedStatement.startPeriod).format("MMM Do ")
                    : "??"
                }
              />
              <ArrowRight className="size-4" />
              <Tag
                icon={CalendarArrowUp}
                label={
                  selectedStatement
                    ? dayjs(selectedStatement.endPeriod).format("MMM Do ")
                    : "??"
                }
              />
            </div>
          </>
        ),
      }}
      tabs={[
        {
          label: "Statement",
          value: "statement",
          sections: [
            {
              children: (
                <ScrollArea ref={ref}>
                  {selectedStatement &&
                    isPdf(selectedStatement.documentUrl) &&
                    selectedStatement.documentUrl && (
                      <PDFViewer
                        width={width || undefined}
                        height={height || undefined}
                        documentUrl={selectedStatement.documentUrl}
                      ></PDFViewer>
                    )}
                  {selectedStatement &&
                    isImage(selectedStatement.documentUrl) &&
                    selectedStatement.documentUrl && (
                      <img
                        className="border border-gray-300"
                        src={selectedStatement.documentUrl}
                        alt={selectedStatement.id}
                        width={width || undefined}
                        height={height || undefined}
                      />
                    )}
                </ScrollArea>
              ),
            },
          ],
        },
        {
          label: "Data",
          value: "data",
          sections: [
            {
              title: "Data",
              children: (
                <>
                  {selectedStatement && <JSONViewer data={selectedStatement} />}
                  {!selectedStatement && <div>No statement selected</div>}
                </>
              ),
            },
          ],
        },
      ]}
    />
  );
};
export { StatementContextPanel };
